import { memo, useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatTime } from "@utils/util";
import {
  selectIsPaused,
  setCurrentArchive,
  setPaused,
} from "@app/player/playerSlice";
import LOCAL_STORAGE from "@utils/localStorage";
import HlsPlayer from "./components/HlsPlayer";
import LiveControls from "@components/live/LiveControls.jsx";
import AndroidPlayer from "./components/AndroidPlayer";
import { useTranslation } from "react-i18next";
import store from "@app/store";
import {
  selectPlayerType,
  selectCurrentChannel,
} from "@app/channels/channelsSlice";
import { useToast } from "@hooks/useToast";
import VodControls from "./components/VodControl";
import {
  setIsPlayerOpen,
  setAudioTracks,
  setResolutions,
  setSubtitles,
  selectSelectedSubtitle,
  selectSelectedQuality,
  selectSelectedAudio,
  selectSubtitles,
  selectAudioTracks,
  selectResolutions,
  setCtrl,
  setRetryCount,
  selectRetryCount,
  selectCtrl,
} from "@app/global";

import { GoogleIMA } from "../../GoogleIMA";

import "./styles/player.scss";
import { setMoviesView, setChannelsView, channelInfo } from "@server/requests";
import { InoButton, Modal, toast } from "@ino-ui/tv";

let timeout = null;

const isHlsUrl = (url) => {
  if (!url) return false;

  if (typeof url === "object") {
    return url.url.includes(".m3u8") || url.includes(".ts");
  }

  return url.includes(".m3u8") || url.includes(".ts");
};

export default memo(function Player({
  type,
  url,
  pipMode,
  setUrl,
  setPipMode,
  refUrlLive,
  endedArchive,
  retryC,
  setRetryC,
  title,
  showProtected,
  onEnded,
  onRememberTime,
  startTime,
  onNextArchive,
  movieId,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const refVideo = useRef(null);
  const refDuration = useRef(null);
  const refCurrentTime = useRef(null);
  const { retryOperation, showToast, hideToast } = useToast();
  const secCurrentTime = useRef(0);
  // const secDuration = useRef(0);
  const lastRememberTimeUpdate = useRef(0);
  const maxRetries = 3;
  const adContainerRef = useRef(null);
  const paused = useSelector(selectIsPaused);
  const navigate = useNavigate();
  const audioTracks = useSelector(selectAudioTracks);
  const subtitles = useSelector(selectSubtitles);
  const resolutions = useSelector(selectResolutions);
  const selectedAudio = useSelector(selectSelectedAudio);
  const selectedSubtitle = useSelector(selectSelectedSubtitle);
  const selectedQuality = useSelector(selectSelectedQuality);
  const [isPlayedOnce, setIsPlayedOnce] = useState(false);
  const [isMovieLoaded, setIsMovieLoaded] = useState(true);
  const [isErrorOccurred, setIsErrorOccurred] = useState(false);

  const [secDuration, setSecDuration] = useState(0);
  const [currentTimeState, setCurrentTimeState] = useState(0);
  const [alreadyRetryed, setAlreadyRetryed] = useState(false);
  const [showAds, setShowAds] = useState(false);
  const [adTagUrl, setAdTagUrl] = useState(
    "https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=",
  ); // Set this from your ad server
  const durationRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const viewDurationRef = useRef(0);
  const isStartRef = useRef(true);
  const intervalRef = useRef(null);
  const isAdPlayingRef = useRef(false);
  const ctrl = useSelector(selectCtrl);
  const retryCount = useSelector(selectRetryCount);

  const [nextRetryStarted, setNextRetryStarted] = useState(false);

  const playerType = useSelector(selectPlayerType);
  const currentChannel = useSelector(selectCurrentChannel);

  const lastTimeRef = useRef(0);
  const bufferingStartTimeRef = useRef(null);
  const totalBufferingTimeRef = useRef(0);

  const retryIntervalRef = useRef(null);

  const [isPlaybackLoaded, setIsPlaybackLoaded] = useState(false);
  const [isArchiveLoaded, setIsArchivedLoaded] = useState(false);

  const archiveInitialTimeRef = useRef(null);

  const watchedTimeRef = useRef(0); // Accumulates watched time in seconds for live
  const lastTickRef = useRef(Date.now()); // Last interval tick timestamp
  const isSeekingRef = useRef(false);
  const seekBlockTimeoutRef = useRef(null);

  const handleBufferingStart = () => {
    if (type === "live") {
      bufferingStartTimeRef.current = Date.now();
    }
  };

  const handleBufferingEnd = () => {
    if (type === "live" && bufferingStartTimeRef.current) {
      const bufferingDuration =
        (Date.now() - bufferingStartTimeRef.current) / 1000; // convert to seconds
      totalBufferingTimeRef.current += bufferingDuration;
      bufferingStartTimeRef.current = null;
      console.log("bufferingDuration", bufferingDuration);
    }
  };

  const play = () => {
    if (!window.Android) {
      refVideo.current?.play();
    } else {
      window.Android.play();
    }
    dispatch(setPaused(false));
    setIsPlaying(true);
  };

  const pause = () => {
    if (!window.Android) {
      refVideo.current?.pause();
    } else {
      window.Android.pause();
    }
    dispatch(setPaused(true));
    setIsPlaying(false);
  };

  const loadedMetadataHandler = useCallback(() => {
    hideToast();

    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }

    if (window.Android) {
      durationRef.current = window.Android.getVideoDuration();
    } else if (refVideo.current) {
      durationRef.current = refVideo.current.duration;
    }

    setRetryC(0);

    if (startTime) {
      if (refVideo.current) {
        refVideo.current.currentTime = startTime;
      }
    }

    if (paused) {
      dispatch(setPaused(false));
    }

    setIsPlaybackLoaded(true);
  }, [startTime, durationRef]);

  const handleTimeUpdate = (currentTime, duration) => {
    if (isSeekingRef.current) {
      return;
    }

    if (!isPlaybackLoaded && (duration > 0 || currentTime > 0)) {
      setIsPlaybackLoaded(true);
    }

    let adjustedCurrentTime = currentTime;
    let adjustedDuration = duration;

    if (playerType === "archive") {
      if (archiveInitialTimeRef.current === null && currentTime > 0) {
        archiveInitialTimeRef.current = currentTime;
      }

      if (archiveInitialTimeRef.current !== null) {
        adjustedCurrentTime = currentTime - archiveInitialTimeRef.current;

        const latestState = store.getState();
        const archive = latestState.player.currentArchive;
        if (archive && archive.stop_ut && archive.start_ut) {
          adjustedDuration = archive.stop_ut - archive.start_ut;
        } else {
          adjustedDuration = duration - archiveInitialTimeRef.current;
        }
      }
    }

    secCurrentTime.current = adjustedCurrentTime;
    setCurrentTimeState(adjustedCurrentTime);
    setSecDuration(adjustedDuration);

    if (playerType === "archive" && !isArchiveLoaded && adjustedDuration > 0) {
      setIsArchivedLoaded(true);
    }

    if (Math.floor(adjustedCurrentTime) >= adjustedDuration - 1) {
      if (type === "vod") {
        // VOD content ended
        onEnded();
        onRememberTime(Math.floor(currentTime), 100, true);
      } else if (playerType === "archive") {
        // Archive content ended - play the next archive if available

        // Get the latest state from redux store
        const latestState = store.getState();
        const nextArchiveToPlay = latestState.player.nextArchive;
        const archives = latestState.player.archives;
        const currentArchive = latestState.player.currentArchive;

        // First check if we have a valid next archive that the system already identified
        if (nextArchiveToPlay && onNextArchive) {
          onNextArchive();
          return;
        }

        // If we don't have a pre-identified next archive, try to find one manually
        if (
          archives &&
          archives.length > 0 &&
          currentArchive &&
          onNextArchive
        ) {
          // Sort archives by start time
          const sortedArchives = [...archives].sort(
            (a, b) => a.start_ut - b.start_ut,
          );

          // Find current archive index
          const currentIndex = sortedArchives.findIndex(
            (archive) =>
              archive.start_ut === currentArchive.start_ut &&
              archive.stop_ut === currentArchive.stop_ut,
          );

          // If we found current archive and it's not the last one
          if (currentIndex >= 0 && currentIndex < sortedArchives.length - 1) {
            const manualNextArchive = sortedArchives[currentIndex + 1];

            // Dispatch this archive as current before playing it
            store.dispatch(setCurrentArchive(manualNextArchive));
            onNextArchive();
            return;
          }
        }
        endedArchive();
      } else {
        endedArchive();
      }
    } else {
      // Regular playback updates
      if (refDuration.current) {
        refDuration.current.innerHTML = formatTime(duration);
      }
      if (refCurrentTime.current) {
        refCurrentTime.current.innerHTML = formatTime(currentTime);
      }

      // Remember time logic for VOD content
      if (type === "vod" && currentTime > 0) {
        const now = Date.now();
        if (now - lastRememberTimeUpdate.current >= 15000) {
          // 15 seconds
          const percent = duration > 0 ? (currentTime / duration) * 100 : 0;
          onRememberTime(Math.floor(currentTime), percent);
          lastRememberTimeUpdate.current = now;
        }
      }
    }
  };

  const streamEnd = () => {
    if (type === "live") {
      if (playerType !== "live") {
        endedArchive();
      }
    }
  };

  const onBack = () => {
    const currentTime = Math.floor(secCurrentTime.current);
    const percent = (currentTime / secDuration) * 100;
    onRememberTime(currentTime, percent, true);

    if (window.Android && type === "vod") {
      window.Android.destroyPlayer();
    }
  };

  const retryPlayback = async () => {
    try {
      if (!window.Android) {
        const video = document.getElementById("video_player");
        if (video) {
          // const currentSrc = video.src;
          const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
          const getChannelInfo = await channelInfo({ id: lastChannelId });
          const parsedResponse = JSON.parse(getChannelInfo);
          const { error, message } = parsedResponse;
          if (!error) {
            video.src = message.url;
            setUrl(message.url);
          }
          video.load();

          // Set the source and URL state back
          // video.src = currentSrc;
          // setUrl(currentSrc);
          await video.play();
        }
      } else {
        window.Android.destroyPlayer();
        // For Android, we use the original url prop since it's managed by the native player
        const lastChannelId = LOCAL_STORAGE.LAST_CHANNEL_ID.GET();
        const getChannelInfo = await channelInfo({ id: lastChannelId });
        const parsedResponse = JSON.parse(getChannelInfo);
        const { error, message } = parsedResponse;

        if (startTime && playerType !== "live") {
          window.Android.initPlayer(message.url, startTime);
          window.Android.play();
        } else {
          window.Android.initPlayer(message.url);
        }
      }
    } catch (err) {
      console.error("Retry playback error:", err);
    }
  };

  const onErrorHandler = async (err) => {
    // if (type === "live" && !refUrlLive.current) {
    //   refUrlLive.current = url;
    // }

    // Start retry interval if not already started
    // if (!retryIntervalRef.current) {
    //   retryIntervalRef.current = setInterval(() => {
    // }, 5000);
    // }
    if (window.Android) {
      retryPlayback();
    } else {
      if (!retryIntervalRef.current) {
        retryIntervalRef.current = setInterval(() => {
          console.warn("retry on error", err);
          setUrl("");
          retryPlayback();
        }, 5000);
      }
    }
  };

  const commitSeek = (time) => {
    if (!isPlaybackLoaded) return;
    if (!Number.isFinite(time)) return;

    if (window.Android) {
      const androidCurrentTime = window.Android.getCurrentTime() || 0;
      const seekDelta = time - secCurrentTime.current;
      const targetTime = androidCurrentTime + seekDelta;
      console.log("target time", targetTime);
      window.Android.seekTo(Math.round(targetTime));
      secCurrentTime.current = time;
      setCurrentTimeState(time);
    } else if (refVideo.current) {
      const actualTime =
        playerType === "archive" && archiveInitialTimeRef.current !== null
          ? time + archiveInitialTimeRef.current
          : time;
      refVideo.current.currentTime = actualTime;
      secCurrentTime.current = time;
      setCurrentTimeState(time);
    }
  };

  const getTracksHandler = (tracksList) => {
    dispatch(setAudioTracks(tracksList.audio));
    dispatch(setSubtitles(tracksList.subtitles));
    dispatch(setResolutions(tracksList.video));
  };

  useEffect(() => {
    if (subtitles) {
      if (window.Android) {
        if (selectedSubtitle && selectedSubtitle.id !== "Off") {
          const foundIndex = subtitles.findIndex(
            (subtitle) => subtitle.id == selectedSubtitle.id,
          );
          window.Android.selectTrack(
            "TEXT",
            subtitles[foundIndex].track_index,
            subtitles[foundIndex].group_index,
          );
        } else if (selectedSubtitle === "Off") {
          window.Android.selectTrack("TEXT", -1, -1);
        }
      }
    }
  }, [subtitles, selectedSubtitle]);

  useEffect(() => {
    if (audioTracks) {
      if (window.Android) {
        if (
          selectedAudio &&
          selectedAudio !== "Default" &&
          audioTracks.length
        ) {
          const foundIndex = audioTracks.findIndex(
            (audio) => audio.id == selectedAudio.id,
          );
          if (foundIndex !== -1) {
            window.Android.selectTrack(
              "AUDIO",
              audioTracks[foundIndex].track_index,
              audioTracks[foundIndex].group_index,
            );
          }
        } else if (selectedAudio === "Default") {
          window.Android.selectTrack("AUDIO", -1, -1);
        }
      }
    }
  }, [audioTracks, selectedAudio]);

  useEffect(() => {
    if (resolutions) {
      if (window.Android) {
        if (
          selectedQuality &&
          selectedQuality !== "Auto" &&
          resolutions.length
        ) {
          const foundIndex = resolutions.findIndex(
            (resolution) => resolution.id == selectedQuality.id,
          );
          if (foundIndex !== -1) {
            window.Android.selectTrack(
              "VIDEO",
              resolutions[foundIndex].track_index,
              resolutions[foundIndex].group_index,
            );
          }
        } else if (selectedQuality === "Auto") {
          window.Android.selectTrack("VIDEO", -1, -1);
        }
      }
    }
  }, [resolutions, selectedQuality]);

  useEffect(() => {
    setIsPlaybackLoaded(false);
    setNextRetryStarted(false);
    dispatch(setRetryCount(0));
    toast.dismiss();
    archiveInitialTimeRef.current = null;

    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      toast.dismiss();
    };
  }, [url]);

  useEffect(() => {
    dispatch(setIsPlayerOpen(true));

    return () => {
      dispatch(setIsPlayerOpen(false));
      dispatch(setCtrl("movieInfo"));
    };
  }, []);

  useEffect(() => {
    if (adContainerRef.current && !window.Android) {
      // GoogleIMA.init({
      //   timeout: 0,
      //   adTagUrl: adTagUrl,
      //   adContainer: adContainerRef.current,
      //   events: {
      //     onAdStarted: () => {
      //       console.log("on add start");
      //       isAdPlayingRef.current = true;
      //       if (refVideo.current) {
      //         refVideo.current.pause();
      //       }
      //     },
      //     onAdEnded: () => {
      //       isAdPlayingRef.current = false;
      //       if (refVideo.current) {
      //         refVideo.current.play();
      //       }
      //     },
      //     onAdError: (err) => {
      //       console.log("on add error");
      //       isAdPlayingRef.current = false;
      //       console.error("Ad error:", err);
      //       if (refVideo.current) {
      //         refVideo.current.play();
      //       }
      //     },
      //   },
      // });
    }

    return () => {
      GoogleIMA.destroy();
    };
  }, [adTagUrl, adContainerRef.current]);

  const sendViewRequest = async (duration) => {
    try {
      if (type === "live") {
        // Use the actual watched time for live
        const watchedTime = Math.max(0, Math.floor(watchedTimeRef.current));
        await setChannelsView({
          is_start: isStartRef.current,
          channel_id: currentChannel?.id,
          view_duration: watchedTime,
        });
        watchedTimeRef.current = 0; // Reset after sending
        totalBufferingTimeRef.current = 0;
      } else {
        await setMoviesView({
          is_start: isStartRef.current,
          movie_id: movieId,
          view_duration: duration,
        });
      }

      if (isStartRef.current) {
        isStartRef.current = false;
      }

      if (window.Android) {
        lastTimeRef.current = window.Android.getCurrentTime() || 0;
      } else {
        lastTimeRef.current = refVideo.current?.currentTime || 0;
      }
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  useEffect(() => {
    if (window.Android) {
      window.Android.onBufferingStart = handleBufferingStart;
      window.Android.onBufferingEnd = handleBufferingEnd;
    }

    // Reset isStartRef and watched time when channel changes
    isStartRef.current = true;
    watchedTimeRef.current = 0;
    lastTickRef.current = Date.now();

    intervalRef.current = setInterval(async () => {
      if (isAdPlayingRef.current) return;

      let currentTime;
      if (window.Android) {
        currentTime = window.Android.getCurrentTime();
      } else {
        if (!refVideo.current) return;
        currentTime = refVideo.current.currentTime;
      }

      // Calculate elapsed real time since last tick
      const now = Date.now();
      const elapsed = (now - lastTickRef.current) / 1000; // seconds
      lastTickRef.current = now;

      // Only accumulate watched time if not paused
      if (!paused) {
        watchedTimeRef.current += elapsed;
      }

      if (type === "live") {
        await sendViewRequest();
      } else {
        // Use the exact current time without flooring to get accurate duration
        const watchedDuration = Math.round(currentTime - lastTimeRef.current);
        if (watchedDuration > 0) {
          await sendViewRequest(watchedDuration);
        }
      }
    }, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (window.Android) {
        window.Android.onBufferingStart = null;
        window.Android.onBufferingEnd = null;
      }
    };
  }, [type === "live" ? currentChannel?.id : movieId, paused]);

  // useEffect(() => {
  //     if (!nextRetryStarted) return;
  //     console.log("nextRetryStarted", nextRetryStarted);

  //     retryIntervalRef.current = setInterval(async () => {
  //         retryPlayback(1);
  //     }, 30000);

  //     return () => {
  //         if (retryIntervalRef.current) {
  //             clearInterval(retryIntervalRef.current);
  //             setNextRetryStarted(false);
  //         }
  //     };
  // }, [nextRetryStarted]);

  const renderPlayer = () => {
    if (!url) return;

    if (LOCAL_STORAGE.DEVICE_OS.GET() === "android") {
      return (
        <AndroidPlayer
          url={url}
          timeUpdate={handleTimeUpdate}
          streamEnd={streamEnd}
          time={startTime}
          startTime={startTime}
          onBack={onBack}
          onError={onErrorHandler}
          onLoadedMetadata={loadedMetadataHandler}
          getTracks={getTracksHandler}
          onErrorHandler={onErrorHandler}
        />
      );
    }

    if (isHlsUrl(url)) {
      return (
        <HlsPlayer
          refVideo={refVideo}
          url={url}
          timeUpdate={handleTimeUpdate}
          streamEnd={streamEnd}
          error={onErrorHandler}
          loadVideo={loadedMetadataHandler}
          startTime={startTime}
        />
      );
    }

    // Fallback to native HTML5 video player for non-HLS URLs
    return (
      <video
        ref={refVideo}
        src={url}
        id="video_player"
        onTimeUpdate={() =>
          handleTimeUpdate(
            refVideo.current?.currentTime,
            refVideo.current.duration,
          )
        }
        onEnded={streamEnd}
        onLoadedMetadata={loadedMetadataHandler}
        onError={onErrorHandler}
        onWaiting={() => console.log("Video is waiting")}
        autoPlay
        playsInline
      />
    );
  };

  return (
    <>
      <Modal
        isOpen={!isMovieLoaded}
        onClose={() => setIsMovieLoaded(false)}
        size="full"
      >
        <div>
          <p className="unable-to-play">
            {t("Unable to play video. Please try again later.")}
          </p>
          <InoButton
            size="medium"
            isActive={!isMovieLoaded}
            onClick={() => {
              setIsMovieLoaded(true);
              dispatch(setIsPlayerOpen(false));
              dispatch(setCtrl("movieInfo"));
              setUrl("");
            }}
            classNames="apk-update_btn centered"
          >
            {t("Close")}
          </InoButton>
        </div>
      </Modal>
      <div ref={adContainerRef} id="adContainerRef"></div>
      <div id="controls_player">
        {type === "live" && !pipMode && (
          <LiveControls
            showProtected={showProtected}
            setUrl={setUrl}
            url={url}
            refUrlLive={refUrlLive}
            setPipMode={setPipMode}
            secCurrentTime={secCurrentTime}
            cTime={currentTimeState}
            commitSeek={commitSeek}
            refVideo={refVideo}
            play={play}
            pause={pause}
            duration={secDuration}
            isPlaybackLoaded={isPlaybackLoaded}
            isArchiveLoaded={isArchiveLoaded}
          />
        )}
        {type === "vod" && !pipMode && (
          <VodControls
            durationRef={refDuration}
            currentTimeRef={refCurrentTime}
            refVideo={refVideo}
            play={play}
            pause={pause}
            onBack={onBack}
            title={title}
            commitSeek={commitSeek}
            duration={secDuration}
          />
        )}
      </div>
      {renderPlayer()}
    </>
  );
});
